import { useCallback, useEffect, useState } from 'react';
import { Box, Button, FormControl, InputLabel, MenuItem, Select, Snackbar, TextField, useTheme } from '@mui/material';
import { DataGrid } from '@mui/x-data-grid';
import { tokens } from '../../theme';
import Header from '../../components/Header';
import { Link, useLocation, useNavigate } from 'react-router-dom';
import EditIcon from '@mui/icons-material/Edit';
import VisibilityIcon from '@mui/icons-material/Visibility';

const TripReportsList = () => {
  const theme = useTheme();
  const colors = tokens(theme.palette.mode);
  const token = localStorage.getItem('token');

  const location = useLocation();
  const navigate = useNavigate();
  const searchParams = new URLSearchParams(location.search);
  const tripFilter = searchParams.get('trip_filter') === 'all' ? 'all' : 'completed';

  const [countryId, setCountryId] = useState(searchParams.get('countryId') || '');
  const [guideId, setGuideId] = useState(searchParams.get('guideId') || '');
  const [departureDate, setDepartureDate] = useState('');

  const [countries, setCountries] = useState([]);
  const [guides, setGuides] = useState([]);

  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(false);
  const [page, setPage] = useState(0);
  const [pageSize, setPageSize] = useState(25);
  const [totalCount, setTotalCount] = useState(0);

  const [openToast, setOpenToast] = useState(false);
  const [toastMessage, setToastMessage] = useState('');

  // Load countries + all guides on mount
  useEffect(() => {
    fetch(`${process.env.REACT_APP_APILINK}/countrynames`, { headers: { token } })
      .then((r) => r.json())
      .then((data) => setCountries(Array.isArray(data) ? data : []))
      .catch(() => {});

    fetch(`${process.env.REACT_APP_APILINK}/guides/all-names`, { headers: { token } })
      .then((r) => r.json())
      .then((data) => setGuides(Array.isArray(data.guides) ? data.guides : []))
      .catch(() => {});
  }, []);

  // When destination changes, load guides who have actual trips there
  useEffect(() => {
    setGuideId('');
    if (!countryId) {
      fetch(`${process.env.REACT_APP_APILINK}/guides/all-names`, { headers: { token } })
        .then((r) => r.json())
        .then((data) => setGuides(Array.isArray(data.guides) ? data.guides : []))
        .catch(() => {});
      return;
    }
    fetch(`${process.env.REACT_APP_APILINK}/guides/by-destination/${countryId}`, { headers: { token } })
      .then((r) => r.json())
      .then((data) => setGuides(Array.isArray(data.guides) ? data.guides : []))
      .catch(() => {});
  }, [countryId]);

  const getData = useCallback(async () => {
    setLoading(true);
    try {
      const query = new URLSearchParams();
      query.append('page', page + 1);
      query.append('limit', pageSize);
      query.append('trip_filter', tripFilter);
      if (countryId) query.append('country_id', countryId);
      if (guideId) query.append('guide_id', guideId);
      if (departureDate) {
        query.append('date_from', departureDate);
        query.append('date_to', departureDate);
      }

      const res = await fetch(
        `${process.env.REACT_APP_APILINK}/api/admin/trip-reports?${query.toString()}`,
        { headers: { token } }
      );
      const data = await res.json();
      if (data.success) {
        setRows((data.trips || []).map((t) => ({ id: `${t.countryId}_${t.departureDate}_${t.guideId}`, ...t })));
        setTotalCount(data.pagination?.total || 0);
      } else {
        setToastMessage(data.message || 'Failed to fetch trip reports');
        setOpenToast(true);
      }
    } catch (e) {
      setToastMessage('Network error fetching trip reports');
      setOpenToast(true);
    } finally {
      setLoading(false);
    }
  }, [page, pageSize, tripFilter, countryId, guideId, departureDate]);

  useEffect(() => {
    getData();
  }, [getData]);

  const handleCountryChange = (val) => {
    setCountryId(val);
    setPage(0);
  };

  const handleClearFilters = () => {
    setCountryId('');
    setGuideId('');
    setDepartureDate('');
    setPage(0);
    navigate(`/trip-reports?trip_filter=${tripFilter}`, { replace: true });
  };

  const columns = [
    { field: 'countryName', headerName: 'Destination', flex: 1 },
    {
      field: 'departureDate',
      headerName: 'Departure Date',
      flex: 1,
      valueFormatter: ({ value }) => (typeof value === 'string' ? value.split('T')[0] : value),
    },
    { field: 'guideName', headerName: 'Guide', flex: 1 },
    { field: 'totalPassengers', headerName: 'Passengers', flex: 0.6 },
    {
      field: 'edit',
      headerName: 'Edit',
      flex: 0.5,
      sortable: false,
      filterable: false,
      renderCell: (params) => {
        const dateOnly =
          typeof params.row.departureDate === 'string'
            ? params.row.departureDate.split('T')[0]
            : params.row.departureDate;
        return (
          <Link to={`/trip-reports/${params.row.countryId}/${dateOnly}/${params.row.guideId}`} state={{ tripFilter }}>
            <Button
              sx={{
                backgroundColor: colors.greenAccent[600],
                color: colors.grey[100],
                ':hover': { backgroundColor: colors.greenAccent[700] },
              }}
              variant="contained"
            >
              <EditIcon />
            </Button>
          </Link>
        );
      },
    },
    {
      field: 'view',
      headerName: 'View',
      flex: 0.5,
      sortable: false,
      filterable: false,
      renderCell: (params) => {
        const dateOnly =
          typeof params.row.departureDate === 'string'
            ? params.row.departureDate.split('T')[0]
            : params.row.departureDate;
        return (
          <Link to={`/trip-reports/${params.row.countryId}/${dateOnly}/${params.row.guideId}/view`} state={{ tripFilter }}>
            <Button
              sx={{
                backgroundColor: colors.blueAccent[600],
                color: colors.grey[100],
                ':hover': { backgroundColor: colors.blueAccent[700] },
              }}
              variant="contained"
              title="View"
            >
              <VisibilityIcon />
            </Button>
          </Link>
        );
      },
    },
  ];

  const filterSubtitle = tripFilter === 'all' ? 'All trips' : 'Completed trips';

  const selectSx = {
    backgroundColor: colors.primary[400],
    color: colors.grey[100],
    '& .MuiOutlinedInput-notchedOutline': { borderColor: colors.grey[700] },
    '&:hover .MuiOutlinedInput-notchedOutline': { borderColor: colors.grey[600] },
  };

  return (
    <Box m="20px">
      <Header title="TRIP REPORTS" subtitle={filterSubtitle} />

      <Box sx={{ mt: 2, mb: 1, display: 'flex', alignItems: 'center', gap: 2, flexWrap: 'wrap' }}>

        {/* Trip filter */}
        <FormControl size="small" sx={{ minWidth: 180 }}>
          <InputLabel>Trip filter</InputLabel>
          <Select
            value={tripFilter}
            label="Trip filter"
            onChange={(e) => {
              const next = new URLSearchParams(location.search);
              next.set('trip_filter', e.target.value);
              setPage(0);
              navigate(`/trip-reports?${next.toString()}`, { replace: true });
            }}
            sx={selectSx}
          >
            <MenuItem value="completed">Completed Trips</MenuItem>
            <MenuItem value="all">All Trips</MenuItem>
          </Select>
        </FormControl>

        {/* Destination */}
        <FormControl size="small" sx={{ minWidth: 200 }}>
          <InputLabel>Destination</InputLabel>
          <Select
            value={countryId}
            label="Destination"
            onChange={(e) => handleCountryChange(e.target.value)}
            sx={selectSx}
          >
            <MenuItem value="">All</MenuItem>
            {countries.map((c) => (
              <MenuItem key={c.CountryID} value={c.CountryID}>
                {c.CountryName}
              </MenuItem>
            ))}
          </Select>
        </FormControl>

        {/* Guide */}
        <FormControl size="small" sx={{ minWidth: 200 }}>
          <InputLabel>Guide</InputLabel>
          <Select
            value={guideId}
            label="Guide"
            onChange={(e) => { setGuideId(e.target.value); setPage(0); }}
            sx={selectSx}
          >
            <MenuItem value="">All Guides</MenuItem>
            {guides.map((g) => (
              <MenuItem key={g.id} value={String(g.id)}>
                {g.name} {g.surname}
              </MenuItem>
            ))}
          </Select>
        </FormControl>

        {/* Departure Date */}
        <TextField
          size="small"
          type="date"
          label="Departure Date"
          value={departureDate}
          onChange={(e) => { setDepartureDate(e.target.value); setPage(0); }}
          InputLabelProps={{ shrink: true }}
          sx={{ minWidth: 170, backgroundColor: colors.primary[400] }}
        />

        {/* Clear */}
        <Button
          size="small"
          variant="outlined"
          onClick={handleClearFilters}
          sx={{ borderColor: colors.grey[600], color: colors.grey[100], height: 40 }}
        >
          Clear
        </Button>
      </Box>

      <Box
        m="10px 0 0 0"
        height="667px"
        sx={{
          '& .MuiDataGrid-root': { border: 'none' },
          '& .MuiDataGrid-cell': { borderBottom: 'none' },
          '& .name-column--cell': { color: colors.grey[100] },
          '& .MuiDataGrid-columnHeaders': { backgroundColor: colors.blueAccent[700], borderBottom: 'none' },
          '& .MuiDataGrid-virtualScroller': { backgroundColor: colors.primary[400] },
          '& .MuiDataGrid-footerContainer': { borderTop: 'none', backgroundColor: colors.blueAccent[700] },
          '& .MuiCheckbox-root': { color: `${colors.greenAccent[200]} !important` },
        }}
      >
        <DataGrid
          rows={rows}
          columns={columns}
          loading={loading}
          disableRowSelectionOnClick
          disableColumnFilter
          paginationMode="server"
          page={page}
          pageSize={pageSize}
          rowCount={totalCount}
          onPageChange={(newPage) => setPage(newPage)}
          onPageSizeChange={(newSize) => { setPageSize(newSize); setPage(0); }}
          rowsPerPageOptions={[10, 25, 50, 100]}
        />
      </Box>

      <Snackbar
        open={openToast}
        autoHideDuration={4000}
        onClose={() => setOpenToast(false)}
        message={toastMessage}
        anchorOrigin={{ vertical: 'top', horizontal: 'center' }}
      />
    </Box>
  );
};

export default TripReportsList;
